import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsForm } from 'components/common/ns-form/NsForm';
import { PsrPromise } from 'components/direct-sales/psr-entry/components/PsrPromise';
import { PsrInfo } from 'components/direct-sales/psr-entry/components/PsrInfo';
import NsContent from 'components/common/ns-content/NsContent';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { PsrList } from 'components/direct-sales/psr-entry/components/PsrList';
import { useNavigation } from 'hooks/useNavigation';
import PsrEntry from './PsrEntry';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsList } from 'components/common/ns-list/NsList';
import { NsCheckboxInput } from 'components/common/ns-checkbox-input/NsCheckboxInput';
import NsInputterEvent from 'components/common/ns-inputter/NsInputterEvent';
import {
  setHasPsr,
  selectHasPsr,
  selectPsrPreferencesArr,
  resetPsrPreferences
} from 'state/psr/psr-slice';
import { stringToBool } from 'utils/misc';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { PARTNERS } from 'config/app-config';
import { AgentScriptingIntro } from './components/AgentScriptingIntro';
import { AgentScriptingInfo } from './components/AgentScriptingInfo';

export const testId = 'psrTestId';

export const Psr = () => {
  const dispatch = useDispatch();
  const { releaseRevampJourney } = useFlags();
  const { next, eventTypes } = useNavigation();
  const psrPreferences = useSelector(selectPsrPreferencesArr, shallowEqual);
  const hasPsr = useSelector(selectHasPsr, shallowEqual);
  const hasAnyPsrPreferences = !!psrPreferences.length;
  const isAgentJourney = useIsLoginEnabledJourney({
    exclude: [PARTNERS.NONDOMESTIC_TRANSFER]
  });

  const handleChange = (event: NsInputterEvent) => {
    const { value } = event.target as JSX.IntrinsicElements['ns-inputter'];
    const boolValue = stringToBool(value);

    dispatch(setHasPsr(!!boolValue));

    !boolValue && dispatch(resetPsrPreferences());
  };

  const handleGoNext = () => {
    next({ eventType: eventTypes.NEXT });

    dispatch(setHasPsr(hasAnyPsrPreferences));
  };

  if (!releaseRevampJourney) {
    return <PsrEntry />;
  }

  return (
    <NsPanel testId={testId}>
      <NsBackCta />
      <NsContent>
        <h1>Priority Services Register</h1>

        <AgentScriptingIntro shouldShow={isAgentJourney} />
      </NsContent>
      <NsContent>
        <p>
          The Priority Services Register (PSR) helps energy and water companies
          support customers who may need extra help due to a health condition,
          mobility, cognitive, sensory, or communication impairment, or if
          someone in the household is under 6 or of pensionable age.
        </p>
        <NsList className="ul-bullet">
          {[
            `Free gas safety check`,
            `Help reading your meter`,
            `Free meter relocation`,
            `Advanced notice of planned power cuts`
          ]}
        </NsList>
      </NsContent>
      <NsContent>
        <NsForm onSubmit={handleGoNext}>
          <PsrPromise />

          <NsCheckboxInput
            heading="Would anyone living in your household benefit from additional support?"
            name="hasPsr"
            onChange={handleChange}
            value={hasPsr || ''}
            options={[
              {
                label: 'Yes, my household would benefit from the PSR.',
                value: 'true'
              }
            ]}
          />

          {hasPsr && (
            <>
              <AgentScriptingInfo shouldShow={isAgentJourney} />
              <PsrList />
              <PsrInfo />
            </>
          )}

          <NsPrimaryCta>
            {!hasPsr || hasAnyPsrPreferences
              ? 'Continue'
              : 'No extra support needed'}
          </NsPrimaryCta>
        </NsForm>
      </NsContent>
    </NsPanel>
  );
};

export default Psr;
